import React from 'react';
import { reportError } from './ErrorReporter';

/**
 * Error Boundary component that catches React errors and reports them to the server.
 * Provides a fallback UI when errors occur.
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log the error and report to server
        console.error('[ErrorBoundary] Caught error:', error, errorInfo);

        // Store error details in state
        this.setState({
            error: error,
            errorInfo: errorInfo
        });

        // Report to server with component stack
        reportError(error, 'ErrorBoundary', {
            componentStack: errorInfo.componentStack,
            errorBoundary: this.props.name || 'Root'
        });
    }

    handleReload = () => {
        window.location.reload();
    };

    handleGoHome = () => {
        window.location.href = '/';
    };

    render() {
        if (this.state.hasError) {
            // Fallback UI
            return (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    padding: '20px',
                    backgroundColor: '#f5f5f5',
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                }}>
                    <div style={{
                        maxWidth: '600px',
                        width: '100%',
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '40px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            fontSize: '48px',
                            marginBottom: '20px'
                        }}>⚠️</div>

                        <h1 style={{
                            fontSize: '24px',
                            fontWeight: '600',
                            marginBottom: '16px',
                            color: '#333'
                        }}>
                            Something went wrong
                        </h1>

                        <p style={{
                            fontSize: '16px',
                            color: '#666',
                            marginBottom: '24px',
                            lineHeight: '1.5'
                        }}>
                            We've encountered an unexpected error. The error has been automatically reported
                            and we'll look into it. Please try reloading the page.
                        </p>

                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details style={{
                                marginBottom: '24px',
                                textAlign: 'left',
                                backgroundColor: '#f8f8f8',
                                padding: '16px',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}>
                                <summary style={{
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    marginBottom: '8px',
                                    color: '#d32f2f'
                                }}>
                                    Error Details (Development Mode)
                                </summary>
                                <pre style={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    fontSize: '12px',
                                    color: '#333',
                                    margin: '8px 0'
                                }}>
                                    {this.state.error.toString()}
                                </pre>
                                {this.state.errorInfo && (
                                    <pre style={{
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        fontSize: '11px',
                                        color: '#666',
                                        marginTop: '8px'
                                    }}>
                                        {this.state.errorInfo.componentStack}
                                    </pre>
                                )}
                            </details>
                        )}

                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'center'
                        }}>
                            <button
                                onClick={this.handleReload}
                                style={{
                                    padding: '12px 24px',
                                    fontSize: '16px',
                                    fontWeight: '500',
                                    color: 'white',
                                    backgroundColor: '#1976d2',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s'
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = '#1565c0'}
                                onMouseOut={(e) => e.target.style.backgroundColor = '#1976d2'}
                            >
                                Reload Page
                            </button>

                            <button
                                onClick={this.handleGoHome}
                                style={{
                                    padding: '12px 24px',
                                    fontSize: '16px',
                                    fontWeight: '500',
                                    color: '#1976d2',
                                    backgroundColor: 'white',
                                    border: '1px solid #1976d2',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                                onMouseOver={(e) => {
                                    e.target.style.backgroundColor = '#f5f5f5';
                                }}
                                onMouseOut={(e) => {
                                    e.target.style.backgroundColor = 'white';
                                }}
                            >
                                Go to Home
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;

